import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Tooltip, Typography, theme } from 'antd';
import { twMerge } from 'tailwind-merge';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useTopDevicesQuery } from '@/services/revenueApi';
import type { PercentageListResponse } from '@/types/api/shows';
import { themeRawValues } from '@/utils/styleUtils';
import { DevicesPercentageListTooltipContent } from './PercentagesList/DevicesPercentageListTooltipContent';
import { PercentagesListEcho } from './PercentagesList/PercentagesListEcho';
import TopDevicesModalEcho from './TopDevicesModalEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';

const colors = { Devices: themeRawValues.colors.chartColors.color3 };

const TopDevicesCardEcho = ({ className }: { className?: string }) => {
  const isBeforeDataAvailability = useIsBeforeDataAvailability('device');
  const { token } = theme.useToken();
  const { queryResult } = useFetchQuery(useTopDevicesQuery, undefined, { sources: [] });

  return (
    <CardShellEcho
      className={twMerge(className, 'flex flex-col gap-1')}
      title="Devices"
      headerOptions={queryResult.data?.data.length ? <TopDevicesModalEcho /> : undefined}
    >
      <Typography.Text
        style={{
          color: token.colorTextSecondary,
          marginBottom: token.margin,
        }}
      >
        By downloads of your podcast on podcast players{' '}
        <Tooltip
          placement="bottomRight"
          title={
            <Flex vertical gap={token.marginXS}>
              <Typography.Text strong style={{ color: token.colorTextLightSolid }}>
                Top devices only available for your audio podcast
              </Typography.Text>
              <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
                The data presented here only accounts for content consumed on podcast player apps
                such as for instance Spotify, Apple podcast or Castbox.
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
                It does not account for YouTube, Instagram and TikTok.
              </Typography.Paragraph>
            </Flex>
          }
        >
          <InfoCircleOutlined
            style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
            aria-label="Top devices information"
          />
        </Tooltip>
      </Typography.Text>
      {isBeforeDataAvailability ? (
        <DataAvailabilityBanner dataType="device" />
      ) : (
        <CardContent
          variant="echo"
          {...queryResult}
          renderData={(data: PercentageListResponse) => <Data data={data.data} />}
        />
      )}
    </CardShellEcho>
  );
};

const Data = ({ data }: { data: PercentageListResponse['data'] }) => {
  const { token } = theme.useToken();

  if (Object.keys(data).length < 1) {
    return <NoDataMessageEcho dataType="data" />;
  }

  return (
    <Flex vertical justify="space-between" gap={token.margin} style={{ height: '100%' }}>
      <PercentagesListEcho
        data={data}
        colors={colors}
        usePercentage={true}
        renderTooltipContent={DevicesPercentageListTooltipContent}
      />
    </Flex>
  );
};

export default TopDevicesCardEcho;
